import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  LightbulbIcon, CaretDownIcon, CaretRightIcon, XIcon, PhoneIcon, MapPinIcon, FileTextIcon, ClockCounterClockwiseIcon,
  CheckCircleIcon, ClockIcon, InfoIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const fmtF = (f) => (f ? String(f).slice(0, 10).split("-").reverse().join("/") : "—");
const fmtN = (v) => Number(v || 0).toLocaleString("es-HN", { maximumFractionDigits: 2 });
const PRIO = {
  ALTA: "bg-red-500/10 text-red-400 border-red-500/30",
  MEDIA: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30",
  BAJA: "bg-white/5 text-text-secondary border-border",
};
const TIPO = {
  "PATRÓN RECURRENTE": "text-fuchsia-300 border-fuchsia-500/40",
  "REFERENCIA HISTÓRICA": "text-blue-300 border-blue-500/40",
  "REPOSICIÓN": "text-cyan-300 border-cyan-500/40",
};
const ESTADO = {
  PENDIENTE: "text-text-secondary border-border",
  POSPUESTA: "text-orange-300 border-orange-500/40",
  GESTIONADA: "text-agro-green border-agro-green/40",
};

/** OPORTUNIDADES DEL MES: qué ofrecer, a quién, cuánto y cuándo (estimaciones con ventas reales). */
export default function CalendarOpportunities({ data, loading, controles, setControles, onChanged }) {
  const navigate = useNavigate();
  const [abiertos, setAbiertos] = useState({});
  const [modal, setModal] = useState(null);      // {tipo: historial|contacto|visita|posponer|gestionar, op, cliente}
  const [verRegla, setVerRegla] = useState(false);

  const porCliente = useMemo(() => {
    const m = new Map();
    (data?.items || []).forEach((o) => {
      if (!m.has(o.cliente_id)) m.set(o.cliente_id, { cliente_id: o.cliente_id, cliente: o.cliente, region: o.region, ops: [] });
      m.get(o.cliente_id).ops.push(o);
    });
    const orden = { ALTA: 0, MEDIA: 1, BAJA: 2 };
    return [...m.values()].map((c) => ({
      ...c,
      fecha: c.ops.reduce((a, o) => (o.fecha_sugerida < a ? o.fecha_sugerida : a), c.ops[0].fecha_sugerida),
      prioridad: c.ops.reduce((a, o) => (orden[o.prioridad] < orden[a] ? o.prioridad : a), "BAJA"),
    })).sort((a, b) => (a.fecha.localeCompare(b.fecha) || orden[a.prioridad] - orden[b.prioridad] || a.cliente.localeCompare(b.cliente)));
  }, [data]);

  const crearProforma = (c) => {
    const items = c.ops.filter((o) => o.en_catalogo && o.estado !== "GESTIONADA")
      .map((o) => ({ producto_id: o.producto_id, cantidad: o.cantidad_sugerida || 1, referencia: o }));
    if (!items.length) { toast.error("Los productos sugeridos no existen en el catálogo actual: no se pueden agregar a una proforma."); return; }
    const nota = "OPORTUNIDAD SUGERIDA (estimación con ventas anteriores): " + items.map((i) =>
      `${i.referencia.producto} ${i.referencia.cantidad_texto}` + (i.referencia.precio_referencia ? ` · precio histórico L ${fmtN(i.referencia.precio_referencia)} (${fmtF(i.referencia.precio_referencia_fecha)})` : "")).join("; ");
    navigate("/ventas/nueva", { state: { prefill: { cliente_id: c.cliente_id, items: items.map(({ producto_id, cantidad }) => ({ producto_id, cantidad })), nota } } });
  };

  const set = (patch) => setControles({ ...controles, ...patch });
  return (
    <div className="bg-card border border-fuchsia-500/30 rounded-lg overflow-hidden" data-testid="oportunidades-mes">
      <div className="p-4 border-b border-border/60 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <LightbulbIcon size={18} className="text-fuchsia-300" weight="fill" />
          <h3 className="font-heading text-base">OPORTUNIDADES DEL MES · {data?.mes_txt} {data?.anio}</h3>
        </div>
        {data && (
          <div className="flex flex-wrap gap-x-4 text-[11px] tracking-wider text-text-muted" data-testid="op-resumen">
            <span>OPORTUNIDADES: <strong className="text-text-primary">{data.total}</strong></span>
            <span>CLIENTES: <strong className="text-text-primary">{data.clientes}</strong></span>
            <span className="text-red-400">ALTA: {data.por_prioridad.ALTA}</span>
            <span className="text-agro-yellow">MEDIA: {data.por_prioridad.MEDIA}</span>
            <span>BAJA: {data.por_prioridad.BAJA}</span>
          </div>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select value={controles.estado} onChange={(e) => set({ estado: e.target.value })} data-testid="op-estado"
            className="bg-app border border-border rounded-md px-2 py-1.5 text-xs">
            <option value="activas">ESTADO: PENDIENTES Y POSPUESTAS</option><option value="PENDIENTE">PENDIENTES</option>
            <option value="POSPUESTA">POSPUESTAS</option><option value="GESTIONADA">GESTIONADAS</option><option value="todas">TODAS</option>
          </select>
          <select value={controles.prioridad} onChange={(e) => set({ prioridad: e.target.value })} data-testid="op-prioridad"
            className="bg-app border border-border rounded-md px-2 py-1.5 text-xs">
            <option value="">PRIORIDAD: TODAS</option><option value="ALTA">ALTA</option><option value="MEDIA">MEDIA</option><option value="BAJA">BAJA</option>
          </select>
          <label className="text-[11px] text-text-secondary flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={controles.incluir_atendidas} onChange={(e) => set({ incluir_atendidas: e.target.checked })} data-testid="op-atendidas" />
            INCLUIR CON COMPRA RECIENTE{data?.ocultas_compra_reciente ? ` (${data.ocultas_compra_reciente} ocultas)` : ""}
          </label>
          <button onClick={() => setVerRegla(!verRegla)} className="text-[11px] font-semibold text-fuchsia-300 flex items-center gap-1" data-testid="op-ver-regla">
            <InfoIcon size={12} /> {verRegla ? "OCULTAR REGLA" : "¿CÓMO SE CALCULA?"}</button>
        </div>
      </div>
      <div className="px-4 py-2 text-[11px] text-fuchsia-200/80 bg-fuchsia-500/5 border-b border-border/40">{data?.aviso}</div>
      {verRegla && (
        <ul className="px-6 py-3 text-xs text-text-secondary list-disc space-y-1 border-b border-border/40" data-testid="op-regla">
          {(data?.regla || []).map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      {loading && !data ? <div className="p-8 text-center text-sm text-text-muted">CARGANDO…</div>
        : !porCliente.length ? <div className="p-10 text-center text-sm text-text-muted" data-testid="op-vacio">NO HAY OPORTUNIDADES CON ESTOS FILTROS</div>
        : (
          <div className="max-h-[720px] overflow-y-auto">
            {porCliente.map((c) => (
              <div key={c.cliente_id} className="border-b border-border/40" data-testid={`op-cliente-${c.cliente_id}`}>
                <div className="px-4 py-2.5 flex flex-wrap items-center gap-2 hover:bg-white/[0.02]">
                  <button onClick={() => setAbiertos({ ...abiertos, [c.cliente_id]: !abiertos[c.cliente_id] })} className="flex items-center gap-2 flex-1 text-left min-w-[240px]"
                    data-testid={`op-toggle-${c.cliente_id}`}>
                    {abiertos[c.cliente_id] ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}
                    <span className="text-xs font-mono text-text-muted w-20">{fmtF(c.fecha)}</span>
                    <span className="text-sm font-medium">{c.cliente}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-border text-text-secondary">{c.region}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIO[c.prioridad]}`}>{c.prioridad}</span>
                    <span className="text-[11px] text-text-muted">{c.ops.length} PRODUCTO{c.ops.length > 1 ? "S" : ""} A OFRECER</span>
                  </button>
                  <button onClick={() => crearProforma(c)} data-testid={`op-proforma-${c.cliente_id}`}
                    className="px-2.5 py-1 text-[10px] font-semibold tracking-wider border border-agro-green/40 text-agro-green rounded flex items-center gap-1">
                    <FileTextIcon size={12} /> CREAR PROFORMA</button>
                </div>
                {abiertos[c.cliente_id] && c.ops.map((o) => (
                  <OportunidadFila key={o.clave} o={o} onAccion={(tipo) => setModal({ tipo, op: o })} />
                ))}
              </div>
            ))}
          </div>
        )}
      {modal && <AccionModal modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />}
    </div>
  );
}

function OportunidadFila({ o, onAccion }) {
  return (
    <div className="mx-4 mb-3 rounded-md border border-border/60 bg-app/30 p-3 space-y-2" data-testid={`op-fila-${o.clave}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] tracking-wider text-text-muted">PRODUCTO A OFRECER</span>
        <span className="text-sm font-semibold">{o.producto}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-border">{o.unidad}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TIPO[o.tipo]}`}>{o.tipo}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIO[o.prioridad]}`}>{o.prioridad}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${ESTADO[o.estado]}`}>{o.estado}</span>
        {o.atrasada && <span className="text-[10px] text-orange-300">CONTACTO SUGERIDO VENCIDO</span>}
        {!o.en_catalogo && <span className="text-[10px] text-text-muted">NO ESTÁ EN EL CATÁLOGO ACTUAL</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <Dato l="CANTIDAD SUGERIDA" v={o.cantidad_texto} fuerte testid="op-cantidad" />
        <Dato l="PRECIO HISTÓRICO DE REFERENCIA" v={o.precio_referencia ? `${fmtL(o.precio_referencia)} · ${fmtF(o.precio_referencia_fecha)}` : "—"} />
        <Dato l="ÚLTIMA COMPRA" v={`${fmtF(o.ultima_compra.fecha)} · ${fmtN(o.ultima_compra.cantidad)} ${o.unidad}`} />
        <Dato l="FECHA SUGERIDA DE CONTACTO" v={fmtF(o.fecha_sugerida)} />
        <Dato l="ACCIÓN SUGERIDA" v={o.accion} fuerte />
      </div>
      <div className="text-xs text-text-secondary"><span className="text-[10px] tracking-wider text-text-muted">MOTIVO DE LA SUGERENCIA: </span>{o.motivo}</div>
      {o.compra_reciente && <div className="text-[11px] text-orange-300">COMPRA RECIENTE: {fmtF(o.compra_reciente.fecha)} · {fmtN(o.compra_reciente.cantidad)} {o.unidad} — la necesidad pudo haberse atendido.</div>}
      {o.precio_referencia && <div className="text-[10px] text-text-muted">El precio histórico es el que se aplicó en esa fecha; no es un precio vigente.</div>}
      <div className="flex flex-wrap gap-1.5 pt-1">
        <Btn onClick={() => onAccion("historial")} icon={ClockCounterClockwiseIcon} testid="op-ver-historial">VER HISTORIAL</Btn>
        <Btn onClick={() => onAccion("contacto")} icon={PhoneIcon} testid="op-contacto">PROGRAMAR CONTACTO</Btn>
        <Btn onClick={() => onAccion("visita")} icon={MapPinIcon} testid="op-visita">PROGRAMAR VISITA</Btn>
        <Btn onClick={() => onAccion("gestionar")} icon={CheckCircleIcon} testid="op-gestionada">MARCAR GESTIONADA</Btn>
        <Btn onClick={() => onAccion("posponer")} icon={ClockIcon} testid="op-posponer">POSPONER</Btn>
      </div>
      {o.seguimiento?.length > 0 && (
        <div className="text-[10px] text-text-muted">SEGUIMIENTO: {o.seguimiento.map((s) => `${fmtF(s.fecha)} ${s.estado}${s.nota ? ` (${s.nota})` : ""} · ${s.usuario}`).join(" | ")}</div>
      )}
    </div>
  );
}

const Dato = ({ l, v, fuerte, testid }) => (
  <div><div className="text-[9px] tracking-wider text-text-muted">{l}</div>
    <div className={fuerte ? "font-semibold text-text-primary" : "text-text-secondary"} data-testid={testid}>{v}</div></div>
);
const Btn = ({ onClick, icon: Icon, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="px-2 py-1 text-[10px] font-semibold tracking-wider border border-border rounded hover:border-fuchsia-500/50 hover:text-fuchsia-200 flex items-center gap-1">
    <Icon size={11} /> {children}</button>
);

function AccionModal({ modal, onClose, onDone }) {
  const navigate = useNavigate();
  const { tipo, op } = modal;
  const [fecha, setFecha] = useState(op.fecha_sugerida);
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const titulos = { historial: "HISTORIAL QUE SUSTENTA LA SUGERENCIA", contacto: "PROGRAMAR CONTACTO", visita: "PROGRAMAR VISITA",
    gestionar: "MARCAR COMO GESTIONADA", posponer: "POSPONER OPORTUNIDAD" };
  const guardar = async () => {
    setSaving(true);
    try {
      if (tipo === "contacto" || tipo === "visita") {
        await api.post("/calendar/collection-schedules", {
          cliente_id: op.cliente_id, fecha, monto: 0, tipo, oportunidad_clave: op.clave,
          productos: [{ producto: op.producto, unidad: op.unidad, cantidad_sugerida: op.cantidad_sugerida }],
          nota: `${tipo === "visita" ? "VISITA" : "CONTACTO"} · OFRECER ${op.producto} (${op.cantidad_texto})${nota ? ` · ${nota}` : ""}`,
        });
        await api.post("/calendar/oportunidades/estado", { clave: op.clave, estado: "PENDIENTE", nota: `${tipo === "visita" ? "Visita" : "Contacto"} programado para ${fmtF(fecha)}` });
        toast.success(`${tipo === "visita" ? "Visita" : "Contacto"} programado para ${fmtF(fecha)} (sin enviar comunicaciones al cliente)`);
      } else if (tipo === "gestionar") {
        await api.post("/calendar/oportunidades/estado", { clave: op.clave, estado: "GESTIONADA", nota });
        toast.success("Oportunidad marcada como GESTIONADA");
      } else if (tipo === "posponer") {
        await api.post("/calendar/oportunidades/estado", { clave: op.clave, estado: "POSPUESTA", nota, posponer_hasta: fecha });
        toast.success(`Oportunidad pospuesta hasta ${fmtF(fecha)}`);
      }
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const vm = useModalForm({ onClose });
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="op-modal">
        <div className="p-4 border-b border-border flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-wider text-text-muted">{titulos[tipo]}</div>
            <h3 className="font-heading text-lg">{op.cliente} · {op.producto} <span className="text-sm text-text-muted">({op.unidad})</span></h3>
            <div className="text-xs text-text-secondary">{op.tipo} · CANTIDAD SUGERIDA: {op.cantidad_texto}</div>
          </div>
          <button onClick={onClose} aria-label="CERRAR" data-testid="op-modal-cerrar"><XIcon size={18} /></button>
        </div>
        {tipo === "historial" ? (
          <div className="overflow-auto p-4 space-y-3">
            <div className="text-xs text-text-secondary">{op.motivo}</div>
            {op.devoluciones_descontadas > 0 && <div className="text-[11px] text-orange-300">Se descontaron {fmtN(op.devoluciones_descontadas)} {op.unidad} devueltas (notas de crédito).</div>}
            <table className="w-full text-xs" data-testid="op-sustento">
              <thead><tr className="text-[10px] tracking-wider text-text-muted text-left border-b border-border">
                <th className="py-1.5">FECHA</th><th>FACTURA</th><th className="text-right">CANTIDAD</th><th className="text-right">PRECIO</th><th className="text-right">VALOR FACTURADO</th><th className="pl-3">ORIGEN</th></tr></thead>
              <tbody>{op.sustento.map((s, i) => (
                <tr key={i} className="border-b border-border/30"><td className="py-1.5">{fmtF(s.fecha)}</td><td className="font-mono">{s.numero}</td>
                  <td className="text-right">{fmtN(s.cantidad)} {s.unidad}</td><td className="text-right">{fmtL(s.precio_unitario)}</td>
                  <td className="text-right">{fmtL(s.valor)}</td><td className="pl-3 text-text-muted">{s.origen}</td></tr>))}</tbody>
            </table>
            <div className="flex justify-end">
              <button onClick={() => navigate(`/reportes?tab=historial&vista=detalle&cliente=${encodeURIComponent(op.cliente_id)}&producto=${encodeURIComponent(op.producto_id)}`)}
                className="px-3 py-1.5 text-[11px] font-semibold border border-agro-green/40 text-agro-green rounded">ABRIR TODO EL HISTORIAL EN REPORTES</button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {tipo !== "gestionar" && (
              <label className="block text-[10px] tracking-wider text-text-muted">{tipo === "posponer" ? "POSPONER HASTA" : "FECHA"}
                <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="op-fecha"
                  className="mt-1 w-full bg-app border border-border rounded-md px-2 py-2 text-sm text-text-primary" /></label>
            )}
            <label className="block text-[10px] tracking-wider text-text-muted">NOTA DE SEGUIMIENTO
              <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={3} data-testid="op-nota"
                className="mt-1 w-full bg-app border border-border rounded-md px-2 py-2 text-sm text-text-primary" /></label>
            {(tipo === "contacto" || tipo === "visita") && <div className="text-[11px] text-text-muted">Se agenda en el calendario como ACTIVIDAD PROGRAMADA. No se envía ninguna comunicación al cliente.</div>}
          </div>
        )}
        {tipo !== "historial" && (
          <div className="p-4 border-t border-border flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm border border-border rounded">CANCELAR</button>
            <button onClick={guardar} disabled={saving} data-testid="op-guardar" className="px-4 py-1.5 text-sm bg-agro-green text-black font-semibold rounded disabled:opacity-50">
              {saving ? "GUARDANDO…" : "GUARDAR"}</button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

